import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchPublishedBlogs } from '../blog/publicBlogApi';
import { formatLongDate } from '../blog/blogForm';
import { siteOrigin, usePageMeta } from '../blog/usePageMeta';
import { BlogBanner, PublicFooter, PublicNav } from '../components/site/PublicChrome';

const PAGE_SIZE = 6;

function pageHref(page) {
  return page <= 1 ? '/blog' : `/blog?page=${page}`;
}

function BlogCard({ post }) {
  const dateLabel = formatLongDate(post.published_at);
  const image = post.featured_image_url || '';
  const href = post.slug ? `/blog/${post.slug}` : '/blog';

  return (
    <article className="col-lg-4 col-md-6 mb-4">
      <Link to={href} style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}>
        <div className="blog-card" style={{
          background: '#fff',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(48, 36, 14, 0.08)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {image ? (
            <img
              src={image}
              alt={post.image_alt || post.title}
              style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div style={{
              height: '200px',
              background: 'linear-gradient(135deg, #a68947 0%, #634f23 100%)',
            }} aria-hidden="true" />
          )}
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', flex: 1 }}>
            {post.category ? (
              <p style={{ margin: '0 0 12px', color: '#a68947', fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {post.category}
              </p>
            ) : null}
            <h2 style={{ fontSize: '1.2rem', lineHeight: 1.4, fontWeight: 650, color: '#1d1d1d', margin: '0 0 12px' }}>
              {post.title}
            </h2>
            <p style={{ color: '#5c5c5c', lineHeight: 1.65, fontSize: '15px', margin: '0 0 18px' }}>
              {post.short_summary}
            </p>
            {dateLabel ? (
              <time dateTime={String(post.published_at || '').slice(0, 10)} style={{ marginTop: 'auto', color: '#8a7340', fontSize: '13px', fontWeight: 600 }}>
                {dateLabel}
              </time>
            ) : null}
          </div>
        </div>
      </Link>
    </article>
  );
}

function Blog() {
  const [searchParams] = useSearchParams();
  const requestedPage = Math.max(1, Number(searchParams.get('page')) || 1);
  const [page, setPage] = useState(requestedPage);
  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setPage(requestedPage);
  }, [requestedPage]);

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setError('');
    fetchPublishedBlogs({ page, limit: PAGE_SIZE })
      .then((result) => {
        if (!active) return;
        setBlogs(result.blogs.filter((item) => item.status !== 'draft'));
        setPagination(result.pagination);
        setStatus('ready');
      })
      .catch((loadError) => {
        if (!active) return;
        setBlogs([]);
        setPagination(null);
        setError(loadError.message || 'Unable to load published case studies.');
        setStatus('error');
      });
    return () => {
      active = false;
    };
  }, [page, reloadKey]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const totalPages = pagination ? Number(pagination.total_pages) || 0 : 0;
  const origin = siteOrigin();
  const canonical = `${origin}${pageHref(page)}`;
  const description = 'Published case studies from BLRC, with the latest judgments and decisions listed first.';
  const title = page > 1 ? `Case Studies – Page ${page} | BLRC` : 'Case Studies | BLRC';
  const jsonLd = useMemo(() => JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'BLRC case studies',
    itemListOrder: 'https://schema.org/ItemListOrderDescending',
    numberOfItems: blogs.length,
    itemListElement: blogs.map((post, index) => ({
      '@type': 'ListItem',
      position: ((page - 1) * PAGE_SIZE) + index + 1,
      url: `${origin}/blog/${post.slug}`,
      name: post.title,
    })),
  }), [blogs, origin, page]);

  usePageMeta({
    title,
    description,
    type: 'website',
    canonical,
    jsonLd,
    prev: page > 1 ? `${origin}${pageHref(page - 1)}` : '',
    next: totalPages > page ? `${origin}${pageHref(page + 1)}` : '',
  });

  const pageNumbers = [];
  if (totalPages > 1) {
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let number = Math.max(1, end - 4); number <= end; number += 1) {
      pageNumbers.push(number);
    }
  }

  return (
    <>
      <PublicNav />
      <BlogBanner
        title="BLRC Blog"
        summary="Judgments and case studies published by the firm, with the newest decisions shown first."
      />
      <section className="blog-content" style={{ padding: '72px 0', background: '#f7f4ee' }}>
        <div className="container">
          {status === 'loading' && (
            <p role="status" style={{ textAlign: 'center', color: '#634f23', padding: '48px 0' }}>
              Loading published case studies…
            </p>
          )}
          {status === 'error' && (
            <div role="alert" style={{ textAlign: 'center', padding: '48px 16px' }}>
              <p style={{ color: '#7a2e2e', marginBottom: '16px' }}>{error}</p>
              <button
                type="button"
                onClick={() => setReloadKey((current) => current + 1)}
                style={{ background: '#a68947', color: '#fff', border: 0, borderRadius: '6px', padding: '10px 18px', fontWeight: 600 }}
              >
                Try again
              </button>
            </div>
          )}
          {status === 'ready' && blogs.length === 0 && (
            <p style={{ textAlign: 'center', color: '#5c5c5c', padding: '48px 16px' }}>
              {page > 1 ? 'There are no published case studies on this page.' : 'No published case studies yet.'}
              {page > 1 ? (
                <>
                  {' '}
                  <Link to="/blog">Back to the first page</Link>
                </>
              ) : null}
            </p>
          )}
          {status === 'ready' && blogs.length > 0 && (
            <div className="row">
              {blogs.map((post) => <BlogCard key={post.id || post.slug} post={post} />)}
            </div>
          )}
          {status === 'ready' && totalPages > 1 && (
            <nav aria-label="Case study pages" style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
              {page > 1 ? (
                <Link rel="prev" to={pageHref(page - 1)} style={pageLinkStyle(false)}>Previous</Link>
              ) : null}
              {pageNumbers.map((number) => (
                <Link
                  key={number}
                  to={pageHref(number)}
                  aria-current={number === page ? 'page' : undefined}
                  style={pageLinkStyle(number === page)}
                >
                  {number}
                </Link>
              ))}
              {page < totalPages ? (
                <Link rel="next" to={pageHref(page + 1)} style={pageLinkStyle(false)}>Next</Link>
              ) : null}
            </nav>
          )}
        </div>
      </section>
      <PublicFooter />
      <style>
        {`
          .blog-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
          .blog-card:hover { transform: translateY(-4px); box-shadow: 0 14px 28px rgba(48, 36, 14, 0.12); }
          @media (max-width: 767px) {
            .blog-header { padding-top: 120px !important; padding-bottom: 48px !important; }
            .blog-content { padding-top: 40px !important; padding-bottom: 40px !important; }
          }
        `}
      </style>
    </>
  );
}

function pageLinkStyle(current) {
  return {
    minWidth: '42px',
    textAlign: 'center',
    padding: '8px 14px',
    borderRadius: '6px',
    textDecoration: 'none',
    fontWeight: 650,
    background: current ? '#a68947' : '#fff',
    color: current ? '#fff' : '#634f23',
    border: '1px solid #e4d7bf',
  };
}

export default Blog;
